import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { Loader2 } from 'lucide-react'
import { cx } from '@/lib/cx'

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger'
type Size = 'md' | 'lg'

interface Props extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  variant?: Variant
  size?: Size
  /** Shows a spinner and blocks interaction without collapsing the layout. */
  busy?: boolean
  fullWidth?: boolean
  leadingIcon?: ReactNode
  trailingIcon?: ReactNode
  children: ReactNode
  className?: string
}

const base =
  'relative inline-flex items-center justify-center gap-2 rounded-2xl font-semibold ' +
  'transition-[transform,background-color,opacity] active:scale-[0.98] ' +
  'disabled:pointer-events-none disabled:opacity-45 ' +
  // Every control clears the 44px minimum touch target from Apple's HIG and
  // WCAG 2.5.5. The old app had 28px icon buttons throughout.
  'min-h-touch'

const variants: Record<Variant, string> = {
  primary: 'bg-brand-600 text-white shadow-lg shadow-brand-600/20 hover:bg-brand-700',
  secondary:
    'bg-[var(--surface-raised)] text-[var(--text-primary)] border border-[var(--border-subtle)] hover:border-[var(--border-strong)]',
  ghost: 'bg-transparent text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)]',
  danger: 'bg-bad-600 text-white shadow-lg shadow-bad-600/20 hover:bg-bad-700',
}

const sizes: Record<Size, string> = {
  md: 'px-4 text-[0.9375rem]',
  lg: 'px-6 py-4 text-base',
}

export function Button({
  variant = 'primary',
  size = 'md',
  busy = false,
  fullWidth = false,
  leadingIcon,
  trailingIcon,
  children,
  className,
  disabled,
  ...rest
}: Props) {
  return (
    <button
      {...rest}
      disabled={disabled === true || busy}
      // Announce the pending state rather than only showing a spinner.
      aria-busy={busy || undefined}
      className={cx(base, variants[variant], sizes[size], fullWidth && 'w-full', className)}
    >
      {/* Keep the label in the layout while busy so the button doesn't resize
          and shift whatever is under the user's thumb. */}
      <span className={cx('inline-flex items-center gap-2', busy && 'invisible')}>
        {leadingIcon}
        {children}
        {trailingIcon}
      </span>
      {busy && (
        <span className="absolute inset-0 grid place-items-center">
          <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
        </span>
      )}
    </button>
  )
}
